import { createContext, useCallback, useContext, useMemo, useState } from "react";
import { AlertUI } from "Reuse/UI"; // UI component to display alerts
import { generateUniqueID } from "utils";

// Create context for alerts
const AlertsContext = createContext();

/**
 * AlertsProvider
 * ------------------
 * Provides alert functionality to the app.
 * Wrap your app or part of it with this provider to use alerts.
 *
 * Exposes:
 * - showAlerts(title, message, type, duration): Show an alert
 * - hideAlerts(id): Remove an alert by ID
 */
export const AlertsProvider = ({ children }) => {
  const [alerts, setAlerts] = useState([]);

  // Show a new alert
  const showAlerts = useCallback((title, message = null, type = "success", duration = 6000) => {
    const id = generateUniqueID();
    const data = { title, message, type, id, duration };
    setAlerts((prev) => [...prev, data]);
  }, []);

  // Hide an existing alert by ID
  const hideAlerts = useCallback((id) => {
    setAlerts((prev) => prev.filter((el) => el.id !== id));
  }, []);

  const value = useMemo(() => ({ showAlerts, hideAlerts }), [showAlerts, hideAlerts]);

  return (
    <AlertsContext.Provider value={value}>
      <AlertUI alerts={alerts} hideAlerts={hideAlerts} />
      {children}
    </AlertsContext.Provider>
  );
};

// Hook to access alert functions
export const useAlerts = () => useContext(AlertsContext);
